<template>
  <VeeForm v-slot="{ errors, handleSubmit, meta }" as="">
    <div
      class="flex flex-col justify-between rounded-md mt-1 w-full bg-critical-secondary bg-opacity-50 border border-critical-secondary border-opacity-20 darkTheme:border-opacity-10 h-full"
    >
      <form autocomplete="off" class="overflow-auto px-4 py-3">
        <VeeField
          v-slot="{ field }"
          v-model="formModel.quantity"
          name="quantity"
          :rules="{
            required: true,
            min: [symbol.minimumOrderQuantity, t('modules.order.order_count')],
            max: [symbol.maximumOrderQuantity, t('modules.order.order_count')],
            quantityTick: symbol.lotSize,
          }"
        >
          <NInput
            v-mask="{
              alias: 'positiveNumber',
            }"
            v-focus
            :model-value="formModel.quantity"
            input-class="text-center font-bold"
            v-bind="field"
            :error="errors.quantity"
            :required-sign="false"
            :show-error="true"
            type="tel"
            dir="ltr"
            class="mb-3"
            required
            size="small"
            :step="symbol.lotSize"
            :min="0"
            @focusin="$event.target.select()"
            @update:model-value="changeQuantity($event)"
          >
            <template #prefix>
              <NText
                variant="caption1"
                weight="medium"
                class="text-neutral-primary"
              >
                {{ t('misc.quantity') }}
              </NText>
            </template>

            <template #postfix>
              <div class="flex justify-center items-center">
                <div class="flex flex-col cursor-pointer">
                  <NumberFormatter
                    #="{ number }"
                    :value="symbol.maximumOrderQuantity"
                  >
                    <NText
                      dir="ltr"
                      tag="p"
                      variant="caption2"
                      @click="changeQuantity(symbol.maximumOrderQuantity ?? 0)"
                    >
                      {{ number }}
                    </NText>
                  </NumberFormatter>
                  <NumberFormatter
                    #="{ number }"
                    :value="symbol.minimumOrderQuantity"
                  >
                    <NText
                      dir="ltr"
                      tag="p"
                      variant="caption2"
                      @click="changeQuantity(symbol.minimumOrderQuantity ?? 0)"
                    >
                      {{ number }}
                    </NText>
                  </NumberFormatter>
                </div>
              </div>
            </template>
          </NInput>
        </VeeField>

        <div class="mb-4 flex items-center justify-between px-3">
          <NText variant="caption1">
            {{ `${t('option.openPositionsCount')}:` }}
          </NText>

          <div
            :class="
              openPositionsCount === 0
                ? 'bg-neutral-secondary text-neutral-secondary'
                : isBuyPosition
                  ? 'bg-brand-secondary text-brand-quaternary'
                  : 'bg-critical-secondary text-critical-primary'
            "
            class="px-2 py-0.5 rounded-md cursor-pointer"
            @click="changeQuantity(openPositionsCount)"
          >
            <NumberFormatter #="{ number }" :value="openPositionsCount">
              <NText variant="caption1">
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </div>

        <div class="mb-4">
          <VeeField
            v-slot="{ field }"
            v-model="formModel.price"
            name="price"
            :rules="{
              required: true,
              between: [
                symbol.lowerPriceThreshold,
                symbol.upperPriceThreshold,
                t('modules.order.order_price'),
              ],
              priceTick: symbol.fixedPriceTick,
            }"
          >
            <NInput
              v-mask="{
                alias: 'positiveNumber',
              }"
              :model-value="formModel.price"
              input-class="text-center font-bold"
              v-bind="field"
              :error="errors.price"
              :required-sign="false"
              size="small"
              :show-error="true"
              type="tel"
              dir="ltr"
              required
              class="!mb-1"
              :min="0"
              :step="symbol.fixedPriceTick"
              :disabled="isLinkToPrice"
              @focusin="$event.target.select()"
              @update:model-value="changePrice($event)"
            >
              <template #prefix>
                <NText
                  variant="caption1"
                  weight="medium"
                  class="text-neutral-primary"
                >
                  {{ t('common.price') }}
                </NText>
              </template>

              <template #postfix>
                <div class="flex justify-center items-center">
                  <div class="flex flex-col cursor-pointer">
                    <NumberFormatter
                      #="{ number }"
                      :value="symbol.upperPriceThreshold"
                    >
                      <NText
                        v-gid="{
                          module: 'order',
                          screen: 'home',
                          section: `optionSellOrderDialog`,
                          component: 'maxPriceButton',
                        }"
                        dir="ltr"
                        tag="p"
                        variant="caption2"
                        @click="changePrice(symbol.upperPriceThreshold)"
                      >
                        {{ number }}
                      </NText>
                    </NumberFormatter>
                    <NumberFormatter
                      #="{ number }"
                      :value="symbol.lowerPriceThreshold"
                    >
                      <NText
                        v-gid="{
                          module: 'order',
                          screen: 'home',
                          section: `optionSellOrderDialog`,
                          component: 'minPriceButton',
                        }"
                        dir="ltr"
                        tag="p"
                        variant="caption2"
                        @click="changePrice(symbol.lowerPriceThreshold)"
                      >
                        {{ number }}
                      </NText>
                    </NumberFormatter>
                  </div>

                  <NegarLinkIcon
                    class="ms-2 cursor-pointer"
                    :class="{ 'text-brand-primary': isLinkToPrice }"
                    @click="changeLink"
                  />
                </div>
              </template>
            </NInput>
          </VeeField>

          <div v-if="!isValidPrice" class="flex">
            <NegarErrorLightIcon class="text-warning-secondary" size="16" />

            <NText variant="overline" class="text-neutral-primary ms-1">
              {{ t('option.fairPrice.validationForSell') }}
            </NText>
          </div>
        </div>

        <VeeField
          v-slot="{ field }"
          v-model="formModel.totalValue"
          name="totalValue"
          :rules="{
            required: true,
          }"
        >
          <NInput
            v-mask="{
              alias: 'positiveNumber',
            }"
            :model-value="formModel.totalValue"
            v-bind="field"
            input-class="text-center font-bold"
            :error="errors.totalValue"
            :required-sign="false"
            :show-error="true"
            type="tel"
            dir="ltr"
            class="mb-4"
            size="small"
            :min="0"
            required
            @focusin="$event.target.select()"
            @update:model-value="changeTotalValue($event)"
          >
            <template #prefix>
              <NText
                variant="caption1"
                weight="medium"
                class="text-neutral-primary"
              >
                {{ t('misc.value') }}
              </NText>
            </template>

            <template #postfix>
              <NText variant="caption1" class="text-neutral-secondary">
                {{ t('misc.rial') }}
              </NText>
            </template>
          </NInput>
        </VeeField>

        <div class="mb-2">
          <NText
            variant="caption1"
            weight="medium"
            tag="div"
            class="mb-1"
            :class="{
              'text-neutral-primary-disable': isSrategyFieldsDisable,
            }"
          >
            {{ t('option.guaranteeMethod') }}
          </NText>

          <VeeField
            v-slot="{ field }"
            v-model="formModel.marginSource"
            name="marginSource"
            :rules="{
              required: true,
            }"
          >
            <template v-if="isSrategyFieldsDisable">
              <NInput
                v-model="formModel.marginSource.label"
                :placeholder="t('option.selectTheSymbol')"
                input-class="text-right"
                disabled
                size="small"
              />
            </template>

            <template v-else>
              <NListbox
                v-bind="{ field }"
                :model-value="formModel.marginSource"
                :required-sign="false"
                required
                class="w-full"
                input-size="small"
                :placeholder="t('option.selectTheSymbol')"
                :disabled="isSrategyFieldsDisable"
                @update:model-value="handleOnMarginSourceChange"
              >
                <NListboxOption
                  v-for="(item, index) of MARGIN_SOURCE_LISTBOX_ITEMS"
                  :key="`${item.value}${index}`"
                  :value="item.value"
                  :label="item.label"
                >
                  <NText variant="caption1" weight="medium">
                    {{ item.label }}
                  </NText>
                </NListboxOption>
              </NListbox>
            </template>
          </VeeField>
        </div>
        <div
          v-if="formModel?.marginSource?.value === MARGIN_SOURCE_CODE.position"
          class="mb-3"
        >
          <NText
            variant="caption1"
            weight="medium"
            tag="div"
            class="mb-1"
            :class="{
              'text-neutral-primary-disable': isSrategyFieldsDisable,
            }"
          >
            {{ t('option.firstUnderluingAsset') }}
          </NText>

          <VeeField
            v-slot="{ field }"
            v-model="formModel.strategyLegs"
            name="strategyLegs"
            :rules="{
              required: true,
            }"
          >
            <NListbox
              v-bind="{ field }"
              v-model="formModel.strategyLegs"
              :required-sign="false"
              required
              class="w-full"
              input-size="small"
              :placeholder="t('option.selectTheSymbol')"
              :disabled="isSrategyFieldsDisable"
            >
              <NListboxOption
                v-for="(asset, index) of optionStrategyLegs"
                :key="`${asset.isin}${index}`"
                :value="asset.isin"
                :label="asset.instrumentName"
              >
                <div class="w-full flex justify-between">
                  <NText variant="caption1" weight="medium">
                    {{ asset.instrumentName }}
                  </NText>

                  <span>
                    <NumberFormatter #="{ number }" :value="asset.strikePrice">
                      <NText variant="caption1" weight="medium" class="me-1">
                        {{ number }}
                      </NText>
                    </NumberFormatter>

                    <NText variant="caption1" weight="medium">
                      {{ t('misc.rial') }}
                    </NText>
                  </span>
                </div>
              </NListboxOption>
            </NListbox>
          </VeeField>
        </div>
        <div
          v-if="formModel.marginSource.value === MARGIN_SOURCE_CODE.cash"
          class="mb-2 flex items-center justify-between px-3"
        >
          <NText variant="caption1">
            {{ `${t('option.marginPrice')}:` }}
          </NText>

          <div>
            <NumberFormatter #="{ number }" :value="marginPrice">
              <NText variant="caption1">
                {{ number }}
              </NText>

              <NText variant="caption1" class="mr-1">
                {{ t('misc.rial') }}
              </NText>
            </NumberFormatter>
          </div>
        </div>
        <div
          v-else-if="
            formModel.marginSource.value === MARGIN_SOURCE_CODE.baseAsset &&
            optionContract?.underlyingAssetName
          "
          class="mb-3 flex items-center justify-between px-3"
        >
          <NText variant="caption1">
            {{ `${t('option.firstUnderluingAsset')}:` }}
          </NText>

          <NText variant="caption1" class="mr-1">
            {{ optionContract?.underlyingAssetName }}
          </NText>
        </div>

        <div
          v-if="showBuyingPower"
          class="mb-4 flex items-center justify-between px-3"
        >
          <NText variant="caption1">
            {{ `${t('option.buyingPower')}:` }}
          </NText>

          <div>
            <NumberFormatter #="{ number }" :value="cash?.optionBuyingPower">
              <NText variant="caption1">
                {{ number }}
              </NText>

              <NText variant="caption1" class="mr-1">
                {{ t('misc.rial') }}
              </NText>
            </NumberFormatter>
          </div>
        </div>

        <div class="mt-4 text-center">
          <NText variant="body">
            {{ VALIDITY_TYPE_LABEL[VALIDITY_TYPES.Day] }}
          </NText>
        </div>
      </form>

      <div
        class="bg-neutral-primary flex gap-2 justify-center items-center w-full p-3 rounded-b-md"
      >
        <NButton
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'optionSellOrderDialog',
            component: 'submitOrderButton',
            disabled: loading || !meta.valid || isOrderStatusPending,
          }"
          variant="fill"
          class="font-bold text-c1 flex-[2]"
          type="submit"
          color="red"
          size="small"
          :loading="loading"
          :disabled="loading || !meta.valid || isOrderStatusPending"
          @click="handleSubmit($event, onSubmit)"
        >
          {{
            isEditOrder
              ? t('modules.order.editOrder')
              : t('modules.order.sendSellOrder')
          }}
        </NButton>

        <NButton
          v-if="!isEditOrder"
          v-gid="{
            module: 'order',
            screen: 'home',
            section: `optionSellOrderDialog`,
            component: 'sendAndCloseButton',
            disabled: loading || !meta.valid || isOrderStatusPending,
          }"
          variant="outline"
          class="font-bold text-c1"
          color="red"
          size="small"
          :disabled="loading || !meta.valid || isOrderStatusPending"
          @click="
            (event) => {
              shouldCloseDialogAfterOrderExecution = true;
              handleSubmit(event, onSubmit);
            }
          "
        >
          {{ t('modules.order.sendAndClose') }}
        </NButton>
      </div>
    </div>
  </VeeForm>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { NegarLinkIcon, NegarErrorLightIcon } from '@negar/icons';
import { NInput, useDialog } from '@negar/core';
import { useFeatureFlag } from '@libs/feature-flag';
import { useLiveCashQuery } from '@core/cash';
import { useSymbolDeductions } from '@core/symbol';
import {
  useOptionPosition,
  useLiveGetOptionContract,
  validatePrice,
  calcSymbolSellValueOption as calcOptionValue,
  calcOptionSymbolQuantitySell,
  useGetStrategiesLegs,
} from '@core/option';
import {
  ORDER_SIDES,
  ORDER_TYPES,
  VALIDITY_TYPE_LABEL,
  VALIDITY_TYPES,
  NEW_OMS_ORDER_STATUSES_GROUPS,
  useLiveOrdersQuery,
} from '@core/order';
import { Instrument, OrderItem } from '@libs/api-reference';
import { omitBy, isEmpty } from '@libs/utils';

import {
  useCreateOrder,
  MARGIN_SOURCE_CODE,
  useOrderDialogs,
  MARGIN_SOURCE_LISTBOX_ITEMS,
} from '@/modules/order';

import NumberFormatter from '@/components/Number.vue';

import vMask from '@/directives/mask';
import vFocus from '@/directives/focus';

import eventBus from '@/composables/event/eventBus';

type FormModel = {
  quantity?: number | null;
  price?: number | null;
  totalValue?: number | null;
  validityDate: Date;
  validityType: number;
  remainingQuantity?: number | null;
  marginSource: {
    value: number;
    label: string;
  };
  strategyLegs?: {
    value: string;
    label: string;
  } | null;
};

type OptionSellOrderFormProps = {
  symbol: Instrument;
  model: {
    quantity?: null | number;
    price?: null | number;
    marginSource: {
      value: number;
      label: string;
    } | null;
    strategyLegs: {
      value: string;
      label: string;
    } | null;
  };
  isEditOrder?: boolean;
  isLinkToPrice?: boolean;
  dialogId?: string;
  isOrderStatusPending?: boolean;
  selectedOrder?: OrderItem;
};

const props = withDefaults(defineProps<OptionSellOrderFormProps>(), {
  dialogId: undefined,
  selectedOrder: undefined,
  marginSource: MARGIN_SOURCE_LISTBOX_ITEMS[0],
});

const { t } = useI18n();

const BASE_ORDER_FORM_MODEL: FormModel = {
  quantity: props.model.quantity,
  price: props.model.price,
  totalValue: null,
  validityDate: new Date(),
  validityType: VALIDITY_TYPES.Day,
  remainingQuantity: props.model.quantity,
  marginSource: props?.model?.marginSource || MARGIN_SOURCE_LISTBOX_ITEMS[0],
  strategyLegs: props?.model?.strategyLegs,
};

const isLinkToPrice = ref(props.isLinkToPrice);
const shouldCloseDialogAfterOrderExecution = ref(false);
const isin = ref(props.symbol.isin as string);

const isStrategies = useFeatureFlag('strategies');
const { openStartegyConfirmDialog } = useOrderDialogs();

const { quantity: openPositionsCount, positionSide } = useOptionPosition({
  isin: props.symbol.isin,
});

const formModel = reactive<FormModel>({ ...BASE_ORDER_FORM_MODEL });

const { selectedDialogId } = useDialog();

const { cashData: cash } = useLiveCashQuery();
const { optionContract } = useLiveGetOptionContract({
  isin,
});

const { optionStrategyLegs } = useGetStrategiesLegs({
  immediate: !!props.symbol.isin,
  isin: computed(() => props.symbol.isin || ''),
});

const { findWageConfigBySymbol } = useSymbolDeductions();

const { isLoadingCreateOrder, createOrder, updateOrder, isLoadingUpdateOrder } =
  useCreateOrder({
    isin: props.symbol.isin as string,
    orderType: ORDER_TYPES.sell,
    selectedOrder: props.selectedOrder,
    instrumentTypeCode: props.symbol.instrumentTypeCode as string,
  });

const loading = computed(
  () => isLoadingCreateOrder.value || isLoadingUpdateOrder.value
);

const isSrategyFieldsDisable = computed(() => {
  if (!isStrategies.value) return true;

  if (!isEmpty(props.selectedOrder)) {
    return !(!props.selectedOrder?.marginSource && marginPrice.value > 0);
  }

  return !isEmpty(formModel.quantity) && marginPrice.value <= 0;
});

const { orders } = useLiveOrdersQuery();

const wage = findWageConfigBySymbol(props.symbol);

const realValue = computed(() =>
  calcOptionValue(
    {
      quantity: formModel.quantity || 0,
      contractSize: optionContract.value?.contractSize || 0,
      lastTradePrice: formModel.price || 0,
    },
    wage
  )
);

const isBuyPosition = computed(() => positionSide.value === ORDER_SIDES.buy);

const quantity = computed(() => {
  let roundUp = false;

  if (formModel.totalValue) {
    roundUp = formModel.totalValue >= cash.value.buyingPower;
  }

  return calcOptionSymbolQuantitySell(
    formModel,
    wage,
    roundUp,
    optionContract.value?.contractSize || 0
  );
});

const openSellOrders = computed(() =>
  orders.value?.filter((order) => {
    const isOpenOrder = NEW_OMS_ORDER_STATUSES_GROUPS.open.includes(
      order.orderStateId
    );

    return (
      order.instrumentIdentification === props.symbol.isin &&
      order.orderSideId === ORDER_SIDES.sell &&
      isOpenOrder
    );
  })
);

const totalQuantityOpenSellOrders = computed(() =>
  openSellOrders.value.reduce(
    (sum, order) => sum + (order.remainingQuantity || 0),
    0
  )
);

const marginPrice = computed(() => {
  const quantity = formModel?.quantity ?? 0;
  const initialMargin = optionContract.value?.initialMargin ?? 0;

  let outputValue = 0;

  const openBuyPostionCounts = isBuyPosition.value
    ? openPositionsCount.value
    : 0;

  if (totalQuantityOpenSellOrders.value < openBuyPostionCounts) {
    const selectedOrderRemainingQuantity =
      props?.selectedOrder?.remainingQuantity ?? 0;

    outputValue =
      (quantity -
        openPositionsCount.value +
        totalQuantityOpenSellOrders.value -
        selectedOrderRemainingQuantity) *
      initialMargin;
  } else {
    outputValue = quantity * initialMargin;
  }

  return Math.max(0, outputValue);
});

const showBuyingPower = computed(
  () => formModel.marginSource?.value === MARGIN_SOURCE_CODE.cash
);

const changeTotalValue = (value: number) => {
  formModel.totalValue = value;

  if (formModel.price) {
    formModel.quantity = quantity.value;
  }
};

const changePrice = (value: number | null) => {
  formModel.price = value;

  if (formModel.quantity) {
    formModel.totalValue = realValue.value;
  } else if (formModel.totalValue) {
    formModel.quantity = quantity.value;
  }
};

const isValidPrice = computed(() => {
  if (!formModel.price || !optionContract.value) return true;

  const fairPrice = optionContract.value.blackScholes?.fairPrice;
  const previousDayClosingPrice = optionContract.value.previousDayClosingPrice;

  return validatePrice(
    formModel.price,
    fairPrice,
    previousDayClosingPrice,
    'sell'
  );
});

const changeQuantity = (value: number) => {
  formModel.quantity = value;

  if (formModel.price) {
    formModel.totalValue = realValue.value;
  }

  if (marginPrice.value <= 0 && !isEmpty(value)) {
    formModel.marginSource = MARGIN_SOURCE_LISTBOX_ITEMS[0];
    formModel.strategyLegs = null;
  }
};

const changeLink = () => {
  isLinkToPrice.value = !isLinkToPrice.value;

  changePrice(headlinePrices.value as number);
};

const onSubmit = (_, { resetForm }) => {
  const resetFormModel = () => {
    Object.assign(formModel, BASE_ORDER_FORM_MODEL);
    resetForm({
      values: { ...BASE_ORDER_FORM_MODEL },
    });
  };

  if (props.selectedOrder) {
    updateOrder({
      formModel,
      resetFormModel,
      shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
      isOption: true,
    });

    return;
  }

  if (marginPrice.value > 0 && !isSrategyFieldsDisable.value) {
    return openStartegyConfirmDialog({
      position: props?.symbol?.instrumentName as string,
      marginSource: formModel.marginSource?.label,
      createOptionStrategySellOrder: () =>
        createOrder({
          formModel,
          resetFormModel,
          shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
          isOption: true,
        }),
    });
  }

  createOrder({
    formModel,
    resetFormModel,
    shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
    isOption: true,
  });
};

const validRangePrice = (price) =>
  props.symbol.upperPriceThreshold && props.symbol.lowerPriceThreshold
    ? !(
        price > props.symbol?.upperPriceThreshold ||
        price < props.symbol?.lowerPriceThreshold
      )
    : true;

// Calculate maximum buy price and minimum sell price in bidAsk table for link to headline
const headlinePrices = computed(() => {
  if (props.symbol?.bidAsk?.length) {
    const validRangeForAskPrice: number[] = [];

    props.symbol?.bidAsk?.map((o) => {
      if (validRangePrice(o.askPrice)) {
        validRangeForAskPrice.push(o.askPrice);
      }
    });

    const maxAskPrice = Math.max(...validRangeForAskPrice);

    return validRangeForAskPrice.length
      ? maxAskPrice
      : props.symbol.lowerPriceThreshold;
  }

  return props.symbol.lastTradePrice;
});

const handleOnMarginSourceChange = (item: { value: number; label: string }) => {
  if (item.value !== MARGIN_SOURCE_CODE.position) {
    formModel.strategyLegs = null;
  }

  formModel.marginSource = item;
};

eventBus.on('executeOrder', (shouldClose) => {
  if (selectedDialogId.value === props.dialogId && !loading.value) {
    shouldCloseDialogAfterOrderExecution.value = shouldClose;

    onSubmit(formModel, {
      resetForm: () => {
        Object.assign(formModel, BASE_ORDER_FORM_MODEL);
      },
    });
  }
});

watch(
  headlinePrices,
  () => {
    if (isLinkToPrice.value) {
      changePrice(headlinePrices.value as number);
    }
  },
  {
    immediate: true,
  }
);

watch(
  () => props.model,
  (newModel) => {
    const sanitizedModel = omitBy(
      newModel,
      (v) => v === null || v === undefined
    );

    if (Object.keys(sanitizedModel).length) {
      Object.assign(formModel, sanitizedModel);
    }

    if (formModel.price) {
      changePrice(formModel.price);
    }

    if (formModel.quantity) {
      changeQuantity(formModel.quantity);
    }
  },
  {
    deep: true,
    immediate: true,
  }
);
</script>
